"use client"

import { useContext } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { useFeatureFlags } from "@/contexts/feature-flags"
import { OrganizationContext } from "@/contexts/organization"
import { useRoles } from "@/contexts/roles"
import { useTheme } from "next-themes"
import { FormattedMessage } from "react-intl"

import { Skeleton } from "@/components/ui/skeleton"

export function SiteAppsNav() {
  const { isFeatureEnabled } = useFeatureFlags()
  const { isAllowed, getRole } = useRoles()
  const { selectedOrganization } = useContext(OrganizationContext)
  const { theme } = useTheme()
  const pathName = usePathname()

  const iconClassnames = (currentPath: string | null) => {
    const base =
      "pt-4 pb-4 h-16 h-[90px] flex justify-center items-center transition-[0.5s] duration-[linear] ease-[all] hover:bg-[rgba(34_12_76)] hover:text-[white]"
    let text = "text-[white]"
    if (theme == "light") {
      text = "text-slate-500"
    }

    if (isActive(currentPath)) {
      text = "text-[white]"
    }
    const active = isActive(currentPath) && "bg-[rgb(17_6_36)]"
    return `${base} ${text} ${active}`
  }

  const isActive = (href: string | null) => {
    return pathName === href
  }
  return (
    <>
      <div
        className={`h-full w-[80px] ${
          theme === "light" ? "bg-slate-300" : "bg-[rgb(23_7_53)]"
        }`}
      >
        <ul>
          {selectedOrganization === null ? (
            <li
              className={
                "duration-[linear] ease-[all] flex h-16 items-center justify-center py-4 text-[white] transition-[0.2s] hover:bg-violet-900"
              }
            >
              <Link
                className="relative flex flex-wrap items-center justify-center"
                href=""
              >
                <img
                  src="https://42x-assets.s3.amazonaws.com/icons/skeleton-ico.svg"
                  className="w-2/5"
                  alt="skeleton"
                />
                <small className="mt-2 block w-full text-center text-xs font-semibold">
                  <Skeleton className="ml-2 h-2 w-[65px]" />
                </small>
              </Link>
            </li>
          ) : (
            <>
              {isFeatureEnabled("workspace") && (
                <li className={`${iconClassnames("/workspace")}`}>
                  <Link
                    className="relative flex flex-wrap items-center justify-center"
                    href="/workspace"
                  >
                    <img
                      src="https://42x-assets.s3.amazonaws.com/icons/assistant.svg"
                      className={`${
                        theme === "light" &&
                        !isActive("/workspace") &&
                        "brightness-[0.6]"
                      } w-2/5`}
                      alt="assistant"
                    />
                    <small className="mt-2 block w-full text-center text-xxs font-semibold">
                      {getRole(selectedOrganization?.id || "") ===
                      "CHAT_ONLY" ? (
                        <FormattedMessage
                          id="chatBuilder"
                          defaultMessage="chatBuilder"
                        />
                      ) : (
                        <FormattedMessage
                          id="assistants"
                          defaultMessage="assistants"
                        />
                      )}
                    </small>
                  </Link>
                </li>
              )}

              {isFeatureEnabled("content") && isAllowed("USER") && (
                <li className={`${iconClassnames("/content")}`}>
                  <Link
                    className="relative flex flex-wrap items-center justify-center"
                    href="/content"
                  >
                    <img
                      src="https://42x-assets.s3.amazonaws.com/icons/folders.svg"
                      className={`${
                        theme === "light" &&
                        !isActive("/content") &&
                        "brightness-[0.6]"
                      } w-2/5`}
                      alt="folders"
                    />
                    <small className="mt-2 block w-full text-center text-xxs font-semibold">
                      <FormattedMessage id="content" defaultMessage="content" />
                    </small>
                  </Link>
                </li>
              )}

              {isFeatureEnabled("search") && isAllowed("USER") && (
                <li className={`${iconClassnames("/search")}`}>
                  <Link
                    className="relative flex flex-wrap items-center justify-center"
                    href="/search"
                  >
                    <img
                      src="https://42x-assets.s3.amazonaws.com/icons/search.svg"
                      className={`${
                        theme === "light" &&
                        !isActive("/search") &&
                        "brightness-[0.6]"
                      } w-2/5`}
                      alt="search"
                    />
                    <small className="mt-2 block w-full text-center text-xxs font-semibold">
                      <FormattedMessage
                        id="similaritySearch"
                        defaultMessage="Similarity_Search"
                      />
                    </small>
                  </Link>
                </li>
              )}

              {isFeatureEnabled("summary") && isAllowed("USER") && (
                <li className={`${iconClassnames("/summary")}`}>
                  <Link
                    className="relative flex flex-wrap items-center justify-center"
                    href="/summary"
                  >
                    <img
                      src="https://42x-assets.s3.amazonaws.com/icons/summary.svg"
                      className={`${
                        theme === "light" &&
                        !isActive("/summary") &&
                        "brightness-[0.6]"
                      } w-2/5`}
                      alt="summary"
                    />
                    <small className="mt-2 block w-full text-center text-xxs font-semibold">
                      <FormattedMessage
                        id="summarize"
                        defaultMessage="summaryDocuments"
                      />
                    </small>
                  </Link>
                </li>
              )}

              <li className={`${iconClassnames("/transcriptions")}`}>
                <Link
                  className="relative flex flex-wrap items-center justify-center"
                  href="/transcriptions"
                >
                  <img
                    src="https://42x-assets.s3.amazonaws.com/icons/transcriptions.svg"
                    className={`${
                      theme === "light" &&
                      !isActive("/transcriptions") &&
                      "brightness-[0.6]"
                    } w-2/5`}
                    alt="transcriptions"
                  />
                  <span
                    className={`absolute -top-2.5 right-2 h-[15px] w-[15px] rounded-[50%] bg-teal-500`}
                  ></span>
                  <small className="mt-2 block w-full text-center text-xxs font-semibold">
                    <FormattedMessage
                      id="transcribe"
                      defaultMessage="transcribe"
                    />
                  </small>
                </Link>
              </li>

              <li className={`${iconClassnames("/images")}`}>
                <Link
                  className="relative flex flex-wrap items-center justify-center"
                  href="/images"
                >
                  <img
                    src="https://42x-assets.s3.amazonaws.com/icons/images.svg"
                    className={`${
                      theme === "light" &&
                      !isActive("/images") &&
                      "brightness-[0.6]"
                    } w-2/5`}
                    alt="images"
                  />
                  <span
                    className={`absolute -top-2.5 right-2 h-[15px] w-[15px] rounded-[50%] bg-teal-500`}
                  ></span>
                  <small className="mt-2 block w-full text-center text-xxs font-semibold">
                    <FormattedMessage id="images" defaultMessage="images" />
                  </small>
                </Link>
              </li>
            </>
          )}
        </ul>
      </div>
    </>
  )
}
